import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useSetAtom } from 'jotai'
import { CheckCircle2, ExternalLink, FileText } from 'lucide-react'
import {
  closeMainPanelOverlayAtom,
  openMainPanelOverlayAtom,
} from '@/stores/mainPanelOverlayStore'
import { chatProgrammaticMessageAtom } from '@/stores/screenataPageContextStore'
import { trpc } from '@/lib/trpc'
import {
  formatToolValue,
  getToolCallId,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolDetailOverlayLink,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const fileIcon = <FileText className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />

type PolicyDraftState =
  | { kind: 'pending' }
  | { kind: 'accepted'; policyDocumentId?: string | null }
  | { kind: 'discarded' }

export function PoliciesListToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const policies: Array<Record<string, unknown>> = Array.isArray(
    state.result?.policies
  )
    ? state.result.policies
    : []
  const count = Array.isArray(state.result?.policies)
    ? state.result.policies.length
    : null
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading policies...'
        completedLabel={
          count !== null
            ? `${plural(count, 'policy', 'policies')} loaded`
            : 'Policies loaded'
        }
        completedDetail={pickString(
          state.args?.statusFilter && state.args.statusFilter !== 'all'
            ? state.args.statusFilter
            : null
        )}
        completedIcon={fileIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          columns={['Policy', 'Requirement', 'Controls']}
          emptyLabel='No policies found'
          items={policies.map((policy) => ({
            id: pickString(policy.slug),
            title: pickString(policy.title) ?? 'Untitled policy',
            meta: [
              policy.necessity,
              Array.isArray(policy.controlRefs)
                ? policy.controlRefs.slice(0, 2).join(', ')
                : null,
            ],
            tone: normalizeTone(policy.necessity),
          }))}
        />
      ) : null}
    </>
  )
}

export function PoliciesGroundToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const groundedBy = pickString(state.result?.groundedBy)
  const claims: Array<Record<string, unknown>> = Array.isArray(
    state.result?.claims
  )
    ? state.result.claims
    : []
  const passages: Array<Record<string, unknown>> = Array.isArray(
    state.result?.passages
  )
    ? state.result.passages
    : []

  // Name the tier: "grounded in 3 claims" and "grounded in policy text" are
  // materially different levels of confidence, and the reply will lean on
  // whichever one it got.
  const completedLabel =
    groundedBy === 'claims'
      ? `Grounded in ${plural(claims.length, 'claim')}`
      : groundedBy === 'passages'
        ? `Grounded in ${plural(passages.length, 'passage')}`
        : groundedBy === 'fullText'
          ? 'Grounded in full policy'
          : 'Policy grounding loaded'

  const items =
    groundedBy === 'claims'
      ? claims.map((claim, index) => ({
          id: null,
          title: pickString(claim.text) ?? `Claim ${index + 1}`,
          subtitle: pickString(claim.systemOfRecord, claim.verification),
          meta: [
            typeof claim.control === 'string' ? claim.control : null,
            typeof claim.confidence === 'string' ? claim.confidence : null,
          ],
        }))
      : passages.map((passage, index) => ({
          id: null,
          title: pickString(passage.section) ?? `Passage ${index + 1}`,
          subtitle: pickString(passage.text),
          meta: [pickString(passage.policySlug)],
        }))

  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Finding the governing policy...'
        completedLabel={completedLabel}
        loadingDetail={pickString(state.args?.query)}
        completedDetail={pickString(
          state.result?.policy?.title,
          state.result?.policy?.slug,
          state.args?.policySlug
        )}
        completedIcon={fileIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview emptyLabel='No policy grounding found' items={items} />
      ) : null}
    </>
  )
}

export function PoliciesGetContentToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading policy...'
        completedLabel='Policy loaded'
        loadingDetail={pickString(state.args?.policySlug)}
        completedDetail={pickString(
          state.result?.title,
          state.result?.policySlug,
          state.args?.policySlug
        )}
        completedIcon={fileIcon}
      />
      {state.isComplete && !isToolError(state) && state.result ? (
        <ToolDetailOverlayLink
          overlayId={getToolCallId(
            part,
            `policy-${pickString(state.result.slug, state.args?.policySlug) ?? 'detail'}`
          )}
          systemDetail={
            pickString(
              state.result.slug,
              state.result.policySlug,
              state.args?.policySlug
            )
              ? {
                  entityType: 'policy',
                  entityId: pickString(
                    state.result.slug,
                    state.result.policySlug,
                    state.args?.policySlug
                  )!,
                  workspaceId: '',
                }
              : null
          }
          payload={{
            entityType: 'policy',
            toolName: 'policiesGetContent',
            title: pickString(state.result.title) ?? 'Untitled policy',
            subtitle: pickString(state.result.slug, state.args?.policySlug),
            description: Array.isArray(state.result.sections)
              ? `${state.result.sections.length} sections`
              : pickString(state.result.section, state.result.source),
            fields: [
              {
                label: 'Status',
                value: formatToolValue(state.result.status),
                tone: normalizeTone(state.result.status),
              },
              {
                label: 'Version',
                value: formatToolValue(state.result.currentVersion),
              },
              {
                label: 'Approved',
                value: state.result.approvedVersion ? 'yes' : null,
              },
              {
                label: 'Source',
                value: formatToolValue(state.result.source),
              },
            ],
            sections: [
              {
                title: 'Content',
                content: formatToolValue(state.result.content),
              },
            ],
          }}
        />
      ) : null}
    </>
  )
}

export function PoliciesGenerateToolMessage({
  part,
  isLastAssistantMessage = false,
}: ToolPartProps) {
  const state = readToolState(part)
  const params = useParams({ strict: false }) as {
    workspaceId?: string
    programId?: string
  }
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const regenerateMutation = useMutation(
    trpc.policy.regenerateNow.mutationOptions()
  )
  const { mutateAsync: regeneratePolicy } = regenerateMutation
  const openOverlay = useSetAtom(openMainPanelOverlayAtom)
  const closeOverlay = useSetAtom(closeMainPanelOverlayAtom)
  const setProgrammaticMessage = useSetAtom(chatProgrammaticMessageAtom)

  const policySlug = pickString(
    state.result?.policySlug,
    state.args?.policySlug
  )
  const programId = pickString(state.result?.programId, state.args?.programId)
  const title = pickString(state.result?.title) ?? policySlug ?? 'Policy draft'
  const content = pickString(state.result?.content) ?? ''
  const draftId = getToolCallId(part, `policy-draft-${policySlug ?? title}`)
  const isDraft =
    state.isComplete && state.result?.action === 'draft' && content.length > 0
  const [localState, setLocalState] = useState<PolicyDraftState>({
    kind: 'pending',
  })

  const handleDiscard = useCallback(() => {
    setLocalState({ kind: 'discarded' })
    setProgrammaticMessage({
      text: `I discarded the generated policy draft "${title}". Don't regenerate or save it again unless I ask.`,
    })
    closeOverlay(draftId)
  }, [closeOverlay, draftId, setProgrammaticMessage, title])

  const handleAccept = useCallback(async () => {
    if (!policySlug) return
    if (!programId) {
      throw new Error('Missing program id for policy regeneration.')
    }
    const prose = pickString(state.result?.prose)
    // The user reviewed this draft and accepted it — that is the explicit
    // "draft with what we have"; skip the unanswered-questions gate.
    const result = await regeneratePolicy({
      programId,
      policySlug,
      force: true,
      ...(prose === 'voice' ? { prose } : {}),
    } as any)
    const policyDocumentId =
      (result as { policyDocumentId?: string | null })?.policyDocumentId ?? null
    setLocalState({ kind: 'accepted', policyDocumentId })
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getPolicies.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getGenerationItems.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getPolicyDraftClaims.queryKey({
        programId,
        policySlug,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getPolicyClaims.queryKey({
        programId,
        policySlug,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.claimTriage.list.queryKey({
        programId,
        policyName: policySlug,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getPolicyDraftAssumptions.queryKey({
        programId,
        policySlug,
      }),
    })
    if (policyDocumentId) {
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyDocContent.queryKey({
          policyDocumentId,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyVersions.queryKey({
          policyDocumentId,
        }),
      })
    }
    setProgrammaticMessage({
      text: `I accepted the generated policy draft "${title}". Policy slug: ${policySlug}${policyDocumentId ? `. Policy document ID: ${policyDocumentId}` : ''}.`,
    })
    closeOverlay(draftId)
  }, [
    closeOverlay,
    draftId,
    programId,
    policySlug,
    queryClient,
    regeneratePolicy,
    setProgrammaticMessage,
    title,
  ])

  const openDraftOverlay = useCallback(() => {
    if (!isDraft || !policySlug) return
    openOverlay({
      kind: 'policy-drift-review',
      id: draftId,
      source: 'chat',
      payload: {
        mode: 'draft',
        title: 'Policy draft review',
        subtitle: policySlug,
        policySlug,
        summaryTitle: title,
        summaryDescription:
          'Generated by Vera. Accepting writes this draft and refreshes linked claim rows.',
        sectionTitle: 'Generated draft',
        sectionDescription: 'Preview of the full policy draft Vera prepared.',
        footerText:
          'Accept only after reviewing the draft. Discard leaves the current policy unchanged.',
        sectionEdits: [
          {
            sectionKey: policySlug,
            section: title,
            replacementText: content,
          },
        ],
        claimDiff: [
          {
            action: 'rewrite',
            claimText: `${state.result?.claimsRecorded ?? 0} linked claim rows will be regenerated from the accepted policy draft.`,
          },
        ],
        onAccepted: handleAccept,
        onDiscarded: handleDiscard,
      },
      onDismiss: undefined,
    })
  }, [
    content,
    draftId,
    handleAccept,
    handleDiscard,
    isDraft,
    openOverlay,
    policySlug,
    state.result?.claimsRecorded,
    title,
  ])

  const autoOpenedRef = useRef(false)
  useEffect(() => {
    if (!isDraft || autoOpenedRef.current) return
    if (localState.kind !== 'pending') return
    if (!isLastAssistantMessage) return
    autoOpenedRef.current = true
    openDraftOverlay()
  }, [isDraft, localState.kind, openDraftOverlay, isLastAssistantMessage])

  useEffect(() => {
    return () => {
      if (isDraft) closeOverlay(draftId)
    }
  }, [closeOverlay, draftId, isDraft])

  if (isDraft) {
    return (
      <div className='my-2 w-full overflow-hidden rounded-md border border-border bg-card'>
        <div className='flex items-center gap-2 border-b border-border bg-surface-subtle px-4 py-2.5'>
          <FileText className='h-4 w-4 shrink-0 text-cobalt-strong' />
          <span className='truncate text-[12px] font-medium text-text-secondary'>
            {title}
          </span>
          <span className='ml-auto inline-flex shrink-0 rounded-sm border border-border bg-card px-2 py-0.5 text-[10px] font-medium text-text-subtle'>
            {localState.kind === 'pending'
              ? 'Draft · awaiting review'
              : localState.kind === 'accepted'
                ? 'Accepted'
                : 'Discarded'}
          </span>
        </div>
        <div className='flex items-center gap-2 px-4 py-2.5'>
          {localState.kind === 'pending' ? (
            <>
              <button
                type='button'
                onClick={openDraftOverlay}
                className='inline-flex items-center gap-1 text-[11px] font-medium text-cobalt-strong hover:underline'
              >
                <ExternalLink className='h-3 w-3' />
                Reopen draft
              </button>
              <span className='text-[10px] text-text-placeholder'>
                Current policy unchanged
              </span>
            </>
          ) : localState.kind === 'accepted' ? (
            <>
              <CheckCircle2 className='h-3.5 w-3.5 text-emerald-600' />
              <span className='text-[11px] text-status-green-strong'>
                Policy regenerated
              </span>
              {params.workspaceId && policySlug ? (
                <button
                  type='button'
                  onClick={() =>
                    navigate({
                      to: '/workspaces/$workspaceId/policies/$policySlug',
                      params: { workspaceId: params.workspaceId!, policySlug },
                    })
                  }
                  className='inline-flex items-center gap-1 text-[11px] font-medium text-cobalt-strong hover:underline'
                >
                  <ExternalLink className='h-3 w-3' />
                  View
                </button>
              ) : null}
            </>
          ) : (
            <span className='text-[11px] text-text-subtle'>
              Draft was not saved.
            </span>
          )}
        </div>
      </div>
    )
  }

  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Preparing policy draft...'
      completedLabel='Policy draft prepared'
      loadingDetail={pickString(state.args?.policySlug, state.args?.controlRef)}
      completedDetail={pickString(
        state.result?.policySlug,
        state.args?.policySlug,
        state.args?.controlRef
      )}
      completedIcon={fileIcon}
    />
  )
}
